import type { StatusEvidence } from '@/hooks/useStatusEvidence';
import type { CSSProperties, ReactElement } from 'react';
import { sideIcon, sideIconCell } from '@/components/dashboard/sideIconCell';
import { StatusBadge } from '@/components/StatusBadge';
import { REGION_FLAGS, type Region } from '@/constants/regions';
import { isSequenceLocked, normalizeStatus, type CanonicalStatus } from '@/constants/status';
import { nextStepFor } from '@/lib/nextStep';
import { formatDate } from '@/lib/format';
import { labelForSequence } from '@/lib/sequenceLabel';
import type { Submission } from '@/api/v2Client';

export interface SubmissionSubRowProps {
  readonly statusEvidence?: StatusEvidence;
  readonly submission: Submission;
  readonly nda: string;
  readonly onActionClick: (submission: Submission) => void;
  // onLifecycleClick removed — Phase dashboard-ux-fixes TODO-035.
  // Lifecycle history is accessible from Submission Details > Lifecycle tab.
  /** Phase status-tracking-drawer (TODO-026) — opens the StatusDrawer for this sequence. */
  readonly onStatusClick?: (submission: Submission) => void;
  /** Phase submission-archive — called when the archive icon is clicked. */
  readonly onArchive?: (submission: Submission) => void;
  /**
   * Phase phase-fix-subrow-reorder — true when the group has ≥2 unlocked
   * sequences (i.e. reordering is possible). Together with the sequence's
   * own non-Complete status this drives whether the ↕ reorder icon shows in
   * the Seq cell, matching V1's `showReorder && !isComplete`.
   */
  readonly canReorder?: boolean;
  /**
   * Phase phase-fix-subrow-reorder — opens the group's reorder modal (the
   * SAME handler the group row uses). Only invoked from the ↕ icon.
   */
  readonly onReorderClick?: () => void;
}

// Statuses that render a green action button, matching V1 colour differentiation (TODO-033).
const GREEN_STATUSES = new Set<CanonicalStatus>(['Ready', 'Complete']);

/** Returns the correct background colour for the action button per V1. */
function actionBgFor(status: string | null | undefined): string {
  const normalized = normalizeStatus(status) as CanonicalStatus;
  return GREEN_STATUSES.has(normalized) ? '#16a34a' : 'var(--primary)';
}

const cellStyle: CSSProperties = { padding: '12px 8px', textAlign: 'center' };

// ── phase-ui-hidden-sequence-visual-state ────────────────────────────────────

/** "Hidden" pill shown beside the sequence label. Matches the app's muted badges. */
const hiddenBadgeStyle: CSSProperties = {
  display: 'inline-block',
  marginLeft: 8,
  padding: '2px 7px',
  borderRadius: 8,
  fontSize: 10,
  fontWeight: 600,
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

/**
 * Open eye — the row is visible; clicking hides it. SVG (not emoji) because the
 * two states must be unmistakable at 14px, which the 📥/📤 tray pair was not.
 * `aria-hidden` — the button carries the accessible name.
 */
function EyeIcon(): ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Slashed eye — the row is hidden; clicking restores it. */
function EyeOffIcon(): ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export function SubmissionSubRow({
  statusEvidence,
  submission,
  nda,
  onActionClick,
  onStatusClick,
  onArchive,
  canReorder = false,
  onReorderClick,
}: SubmissionSubRowProps): ReactElement {
  const flag = (REGION_FLAGS as Record<string, string>)[submission.Region as Region] ?? '';
  // phase-ui-hidden-sequence-visual-state (Sharon, 2026-07-15) — a hidden
  // sequence only ever appears while the "Show hidden" toggle is on. It must be
  // unmistakable at a glance: de-emphasised row + a "Hidden" badge + an
  // unhide-shaped control. `IsArchived` is already returned per row by
  // GET /api/v1/submissions, so no payload change was needed.
  const isHidden = submission.IsArchived === true;
  // phase-dashboard-lock-consistency (Sharon, 2026-07-23) — the row used to
  // decide BOTH the padlock and the ↕ reorder affordance from
  // `normalizeStatus(...) === 'Complete'`. That was a third, weaker definition
  // of "locked": it ignored the explicit `IsLocked` flag AND every locked
  // status except Complete. On Velbaxin/NDA 200111 sequence 0002 (Status
  // 'Submitted', IsLocked=true) the row therefore drew no padlock and DID
  // offer reorder — inviting an action the reorder dialog itself refuses, and
  // presenting an immutable submitted sequence as editable.
  //
  // Both now come from the ONE shared predicate `isSequenceLocked`, which
  // mirrors the server's `isSubmissionLocked()` exactly. The dialog uses the
  // same helper, so row and dialog can no longer disagree.
  const locked = isSequenceLocked(submission);
  const inProgress = normalizeStatus(submission.Status) === 'Processing' ||
    normalizeStatus(submission.Status) === 'Classifying';
  // Reorder is ABSENT (not disabled) on a locked sequence — the app's
  // established convention for actions that are not merely unavailable but
  // inapplicable (cd2259b).
  const showReorder = canReorder && !locked && onReorderClick !== undefined;
  const label = nextStepFor(submission.Status, statusEvidence) ?? 'Open';
  const btnBg = actionBgFor(submission.Status);

  return (
    <tr
      style={{
        background: 'var(--surface2)',
        cursor: 'pointer',
        // Hidden rows read as de-emphasised but stay fully interactive
        // (clickable, hide/unhide + reorder controls all still work).
        ...(isHidden ? { opacity: 0.55, color: 'var(--text-muted)' } : {}),
      }}
      data-hidden={isHidden ? 'true' : 'false'}
      onClick={() => onActionClick(submission)}
      data-testid={`sub-row-${submission.Id}`}
    >
      <td style={{ ...cellStyle, width: 24 }}></td>
      {/* phase-ui-dashboard-subrow-sequence-identity (Sharon, 2026-07-15) —
          this cell now names what the SEQUENCE is ("Original Application",
          "Supplement — CMC Update", "Annual Report"), so an application's
          history reads like a story. It previously showed a bare "↳ —".

          It does NOT show the Application Type (NDA/ANDA/…): that is an
          APPLICATION-level attribute, identical on every sequence, which
          4bd192f (phase-fix-dashboard-apptype-subrow) removed from here for
          exactly that reason. `labelForSequence` deliberately excludes it —
          see web/src/lib/sequenceLabel.ts for the priority order. Styling
          stays secondary/muted so the group header remains dominant. */}
      <td data-card-title="true"
        style={{
          ...cellStyle,
          paddingLeft: 18,
          fontSize: 12,
          color: 'var(--text-muted)',
          textAlign: 'left',
          // phase-ui-dashboard-stable-columns-artifact-cleanup — the table is
          // now table-layout:fixed, so a long sequence label must truncate
          // INSIDE the Drug column instead of resizing it (Sharon's Genrivex
          // expand screenshots). Full text stays available via the title
          // tooltip below; the ↳ elbow glyph is preserved.
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={labelForSequence(submission)}
        data-testid={`sub-row-sequence-label-${submission.Id}`}
      >
        ↳ {labelForSequence(submission)}
        {/* phase-ui-hidden-sequence-visual-state — the "Hidden" badge sits next
            to the sequence label so the state is legible without hunting for
            the icon. Styled like the app's other muted badges. */}
        {isHidden && (
          <span data-testid={`hidden-badge-${submission.Id}`} style={hiddenBadgeStyle}>
            Hidden
          </span>
        )}
      </td>
      <td data-label="Application"
        className="col-shaded"
        style={{ ...cellStyle, fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 12 }}
      >
        {nda}
      </td>
      <td data-label="Region" style={{ ...cellStyle, fontSize: 13, whiteSpace: 'nowrap' }}>
        {flag} {submission.Region}
      </td>
      <td data-label="Status"
        className="col-shaded"
        style={{ ...cellStyle, ...(onStatusClick ? { cursor: 'pointer' } : {}) }}
        onClick={onStatusClick ? (e) => { e.stopPropagation(); onStatusClick(submission); } : undefined}
        data-testid={onStatusClick ? `status-badge-clickable-${submission.Id}` : undefined}
        title={onStatusClick ? 'Click to view status history' : undefined}
      >
        <StatusBadge status={submission.Status} evidence={statusEvidence} />
      </td>
      {/* Seq cell — phase-fix-subrow-reorder kept V1's [↕][number][🔒]
          content; phase-dashboard-polish-batch (Sharon 2026-07-19) makes the
          NUMBER stay centered regardless of which icons render: the shared
          side-icon structure puts the ↕ reorder in the LEFT gutter and the
          🔒 lock in the RIGHT gutter (one per side — the two are mutually
          exclusive today, ↕ only on incomplete rows and 🔒 only on Complete
          ones, but the layout holds even if both ever render). */}
      <td data-label="Sequence"
        style={sideIconCell({ ...cellStyle, color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' })}
        data-testid={`sub-row-seq-cell-${submission.Id}`}
      >
        {showReorder && (
          <button
            type="button"
            onClick={(e) => {
              // Stop the click from triggering the row's onActionClick.
              e.stopPropagation();
              onReorderClick?.();
            }}
            aria-label={`Reorder sequences for ${nda}`}
            title="Reorder sequences"
            data-testid={`sub-reorder-icon-${submission.Id}`}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              color: 'var(--text-muted)',
              opacity: 0.4,
              padding: 0,
              lineHeight: 1.4,
              transition: 'opacity 0.15s',
              ...sideIcon('left', 2),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.4';
            }}
          >
            ↕
          </button>
        )}
        {submission.SequenceNumber}
        {locked && (
          <span
            style={{ fontSize: 10, ...sideIcon('right', 4) }}
            // Name the ACTUAL status — the tooltip used to read "Locked
            // (Complete)" unconditionally, which would have been a lie on a
            // Submitted/Approved/Rejected sequence.
            title={`Locked (${normalizeStatus(submission.Status)})`}
            data-testid={`sub-lock-icon-${submission.Id}`}
          >
            🔒
          </span>
        )}
      </td>
      {/* phase-dashboard-honest-counts — each sequence shows ITS OWN document
          count: this sequence's CURRENT documents (deleted rows and versions
          superseded by a later replace are not counted — so a document
          replaced in a later sequence counts once, in the replacing
          sequence). Was a hardcoded dash, which made the parent total
          unverifiable on screen. */}
      <td data-label="Documents"
        className="col-shaded"
        data-testid={`sub-row-docs-${submission.Id}`}
        title="Current documents in this sequence (replaced versions are not counted)"
        style={{ ...cellStyle, color: 'var(--text-muted)', fontSize: 12 }}
      >
        {submission.TotalDocs != null ? submission.TotalDocs : '—'}
      </td>
      <td data-label="Next step"
        style={{ ...cellStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        onClick={(e) => {
          e.stopPropagation();
          onActionClick(submission);
        }}
      >
        {inProgress ? (
          <span style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 600, whiteSpace: 'nowrap' }}>
            <span
              className="spinner"
              style={{ width: 10, height: 10, display: 'inline-block', marginRight: 4 }}
              aria-hidden="true"
            />
            In progress
          </span>
        ) : (
          <button
            type="button"
            className="next-step-btn"
            onClick={(e) => {
              e.stopPropagation();
              onActionClick(submission);
            }}
            style={{
              fontSize: 11,
              padding: '6px 14px',
              background: btnBg,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        )}
      </td>
      {/* Updated cell — phase-dashboard-hide-in-date-column (Sharon
          2026-07-19): the hide/unhide eye lives here, beside the date, via
          the shared side-icon structure (sideIconCell.ts): symmetric 28px
          gutters keep the date centered by construction, and the absolutely
          positioned eye in the right gutter can never reflow the date or
          change the row height. Same button, same handler, same
          testid/aria-label as always — relocation only.
          (phase-ui-hidden-sequence-visual-state: visible row → open eye
          "Hide submission"; hidden row → slashed eye "Unhide submission".) */}
      <td data-label="Updated"
        className="col-shaded"
        style={sideIconCell({
          ...cellStyle,
          color: 'var(--text-muted)',
          fontSize: 12,
          whiteSpace: 'nowrap',
          padding: '12px 28px',
        })}
        data-testid={`sub-row-updated-cell-${submission.Id}`}
      >
        {formatDate(submission.UpdatedAt)}
        {onArchive && (
          <button
            type="button"
            data-testid={`archive-btn-${submission.Id}`}
            data-hidden-state={isHidden ? 'hidden' : 'visible'}
            title={isHidden ? 'Unhide submission' : 'Hide submission'}
            onClick={(e) => {
              e.stopPropagation();
              onArchive(submission);
            }}
            style={{
              fontSize: 14,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 4px',
              color: 'var(--text-muted)',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              ...sideIcon('right', 4),
            }}
            aria-label={isHidden ? 'Unhide submission' : 'Hide submission'}
          >
            {isHidden ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </td>
    </tr>
  );
}
